<script lang="ts">
  import { tick } from 'svelte'
  import ActorFollowers from '#activities/components/actor_followers.svelte'
  import app from '#app/app'
  import { isPlainObject } from '#app/lib/boolean_tests'
  import { icon } from '#app/lib/icons'
  import { imgSrc } from '#app/lib/image_source'
  import { onChange, track } from '#app/lib/svelte/svelte'
  import { treq } from '#app/lib/treq'
  import type { SerializedShelvesByIds } from '#app/types/shelf'
  import Modal from '#components/modal.svelte'
  import InventoryBrowser from '#inventory/components/inventory_browser.svelte'
  import { getInventoryView } from '#inventory/components/lib/inventory_browser_helpers'
  import type { InventoryView } from '#inventory/components/lib/inventory_browser_helpers'
  import type { FocusedSection, OnSelectShelf } from '#inventory/types/inventory_browser'
  import type { OrderedCollection } from '#server/types/activity'
  import ShelfEditor from '#shelves/components/shelf_editor.svelte'
  import ShelfItemsAdder from '#shelves/components/shelf_items_adder.svelte'
  import type { SerializedShelf } from '#shelves/lib/shelves'
  import { i18n, I18n } from '#user/lib/i18n'
  import AdminInfo from '#users/components/admin_info.svelte'
  import type { SerializedUser } from '#users/lib/users'
  import { serializeShelfData } from './lib/shelves.ts'

  let {
    shelf = $bindable(),
    withoutShelf = false,
    user,
    itemsShelvesByIds,
    focusedSection,
    showShelfFollowers = $bindable(),
    onSelectShelf,
    onCloseShelf,
  }: {
    shelf?: SerializedShelf
    withoutShelf?: boolean
    user: SerializedUser
    itemsShelvesByIds: SerializedShelvesByIds
    focusedSection: FocusedSection
    showShelfFollowers?: boolean
    onSelectShelf: OnSelectShelf
    onCloseShelf?: () => void
  } = $props()

  let itemsCount: number
  let shelfBoxEl: HTMLElement = $state()
  const isMainUser = $derived(user.isMainUser)
  const fediversable = $derived(user.fediversable)

  const serializedShelfData = $derived(serializeShelfData(shelf, withoutShelf))
  const name = $derived(serializedShelfData.name)
  const description = $derived(serializedShelfData.description)
  const pathname = $derived(serializedShelfData.pathname)
  const picture = $derived('picture' in serializedShelfData ? serializedShelfData.picture : undefined)
  const visibility = $derived('visibility' in serializedShelfData ? serializedShelfData.visibility : undefined)
  const isEditable = $derived('isEditable' in serializedShelfData ? serializedShelfData.isEditable : undefined)
  const iconData = $derived('iconData' in serializedShelfData ? serializedShelfData.iconData : undefined)
  const iconLabel = $derived('iconLabel' in serializedShelfData ? serializedShelfData.iconLabel : undefined)

  let showShelfEditor = $state(false)
  let showShelfItemsAdder = $state(false)

  async function onFocus () {
    if (!shelfBoxEl) await tick()
    app.navigate(pathname, { pageSectionElement: shelfBoxEl })
  }

  async function closeShelfAdder () {
    showShelfItemsAdder = false
  }

  const inventoryViewPromise: Promise<InventoryView> = $derived.by(() => {
    // Track showShelfItemsAdder to refresh inventoryViewPromise once the adder is done
    track(showShelfItemsAdder)
    if (isPlainObject(shelf)) {
      return getInventoryView('shelf', shelf)
    } else if (withoutShelf) {
      return getInventoryView('without-shelf')
    }
  })

  function showFollowersModal () {
    showShelfFollowers = true
    app.navigate(`/shelves/${shelf._id}/followers`)
  }

  async function getFollowersCount (currentShelf) {
    if (fediversable && currentShelf) {
      // Visibility will be accessible only by the shelf owner
      if ('visibility' in currentShelf && !currentShelf.visibility.includes('public')) return
      const name = `shelf-${currentShelf._id}`
      try {
        const res = await treq('activitypub', 'get', 'followers', { name }) as OrderedCollection
        return res.totalItems
      } catch (err) {
        // A 404 will be throwned if the shelf is not public
        // (The guard above should make it unlikely, unless the shelf visibility data is out-of-sync)
        if (err.statusCode !== 404) throw err
      }
    }
  }

  const followersCountByShelfId = $state({})
  function getShelfFollowersCount () {
    if (shelf?._id) {
      // Memoize the promise, so that multiple calls to onChange don't trigger multiple server requests
      followersCountByShelfId[shelf._id] ??= getFollowersCount(shelf)
    }
  }

  function closeFollowersModal () {
    showShelfFollowers = false
    app.navigate(`/shelves/${shelf._id}`)
  }

  $effect(() => {
    onChange(shelf, getShelfFollowersCount)
  })
  $effect(() => {
    if (focusedSection.type === 'shelf') onFocus()
  })
</script>

<div class="full-shelf-box">
  <div class="shelf-box" bind:this={shelfBoxEl}>
    <div class="header">
      {#if withoutShelf}
        <div class="without-shelf-picture">...</div>
      {:else}
        <div class="picture" style:background-image="url({imgSrc(picture, 160)})"></div>
      {/if}
      <button
        class="close-shelf-small close-button"
        title={I18n('unselect shelf')}
        onclick={onCloseShelf}
      >
        {@html icon('close')}
      </button>
    </div>
    <div class="info-box">
      <div>
        <h3 class="name">{name}</h3>
        {#if shelf}
          <ul class="data">
            {#if itemsCount}
              <li>
                <span>{@html icon('book')}{i18n('books')}</span>
                <span class="count">{itemsCount}</span>
              </li>
            {/if}
            {#if visibility}
              <li id="listing" title={i18n('Visible by')}>
                {@html icon(iconData.icon)} {i18n(iconLabel)}
              </li>
            {/if}
            {#await followersCountByShelfId[shelf._id] then followersCount}
              {#if followersCount}
                <li class="followers-count">
                  <a href="/shelves/{shelf._id}/followers" onclick={showFollowersModal}>
                    <span>{@html icon('address-book')}{i18n('followers')}</span>
                    <span class="count">{followersCount}</span>
                  </a>
                </li>
              {/if}
            {/await}
          </ul>
        {/if}
        <p class="description">{description}</p>
        {#if shelf}
          <AdminInfo id={shelf._id} created={shelf.created} />
        {/if}
      </div>
      <div class="actions">
        <button
          class="close-shelf close-button"
          title={I18n('unselect shelf')}
          onclick={onCloseShelf}
        >{@html icon('close')}</button>
        <div class="buttons">
          {#if isEditable}
            <button
              class="show-shelf-edit tiny-button light-blue"
              aria-haspopup="dialog"
              onclick={() => showShelfEditor = true}
            >
              {@html icon('pencil')}
              {I18n('edit shelf')}
            </button>
            <button
              class="tiny-button light-blue"
              aria-haspopup="dialog"
              onclick={() => showShelfItemsAdder = true}
              title={I18n('add books to this shelf')}
            >
              {@html icon('plus')} {I18n('add books')}
            </button>
          {/if}
        </div>
      </div>
    </div>
  </div>

  {#if inventoryViewPromise}
    {#if withoutShelf}
      <InventoryBrowser
        {inventoryViewPromise}
        {isMainUser}
      />
    {:else}
      {#key inventoryViewPromise}
        <InventoryBrowser
          {inventoryViewPromise}
          {isMainUser}
          shelfId={shelf._id}
          {itemsShelvesByIds}
          {onSelectShelf}
        />
      {/key}
    {/if}
  {/if}
</div>

{#if showShelfEditor}
  <Modal onCloseModal={() => showShelfEditor = false}>
    <ShelfEditor
      bind:shelf
      onShelfEditorDone={() => showShelfEditor = false}
    />
  </Modal>
{/if}

{#if showShelfItemsAdder}
  <Modal onCloseModal={closeShelfAdder}>
    <ShelfItemsAdder
      {shelf}
      onShelfItemsAdderDone={closeShelfAdder}
    />
  </Modal>
{/if}

{#if showShelfFollowers}
  <Modal onCloseModal={closeFollowersModal}>
    <ActorFollowers
      actorName="shelf-{shelf._id}"
      actorLabel={shelf.name}
      standalone={true}
    />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .full-shelf-box{
    // Make sure it is possible to scroll to put the shelf box at the top of the viewport
    min-height: 100vh;
  }
  .shelf-box{
    margin: 0.5em 0 0;
    @include display-flex(row);
    background-color: $light-grey;
  }
  .picture{
    // TODO: shelf picture
    width: 10em;
    height: 10em;
  }
  .info-box{
    flex: 1;
    padding: 1em;
    @include display-flex(row, top, space-between);
    .name, .description{
      // make button unbreakable
      flex: 1 0 0;
    }
  }
  .actions{
    @include display-flex(column, flex-end);
  }
  .buttons{
    margin-block-start: 0.5em;
    @include display-flex(column, stretch, center);
    > button{
      @include display-flex(row, center, space-between);
      margin-block-end: 0.5em;
      line-height: 1.6rem;
      min-width: 10em;
    }
  }
  .show-shelf-edit{
    margin-inline-start: auto;
  }
  .data{
    @include display-flex(row, flex-start);
    color: #666;
    margin-block-end: 0.5em;
    li{
      margin-inline-end: 1em;
    }
  }
  .followers-count a{
    cursor: pointer;
    color: #666;
  }
  .count{
    padding-inline-start: 0.5em;
  }
  .close-shelf-small{
    display: none;
  }
  .close-button{
    font-size: 1.5rem;
    @include text-hover($grey, $dark-grey);
  }
  .without-shelf-picture{
    @include display-flex(row, center, center);
    font-size: 1.5rem;
    height: 3em;
    width: 3em;
    color: $grey;
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .shelf-box{
      @include display-flex(column, center, center);
    }
    .header{
      @include display-flex(row, top, center);
    }
    .picture{
      // TODO: shelf picture
      width: 3em;
      height: 3em;
      margin-block-start: 0.5em;
    }
    .close-shelf-small{
      padding: 0.5em 0.3em 0 0;
      position: absolute;
      inset-inline-end: 0.3em;
      display: block;
    }
    .name, .description{
      text-align: center;
    }
    .info-box{
      @include display-flex(column, center, center);
      padding: 0;
      margin-block-end: 0.7em;
    }
    .close-shelf{
      display: none;
    }
    .data{
      @include display-flex(column, center, center);
      li{
        margin: 0.2em 0;
      }
    }
  }
</style>
